import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import DeleteIcon from '@material-ui/icons/Delete';
import { useFormContext } from 'react-hook-form';
import { isEmpty, isFunction, map, merge, without } from 'lodash';
import { t } from '@lingui/macro';
import DataTable from '../../../data-table/DataTable';
import { useSelectorStyles } from '../../../form/ListSelectorStyles';
import { SELECTIONMODE_MULTI, SELECTIONMODE_NONE } from '../../../data-table/SelectionContext';
import ListSelectorDialog from '../../../form/ListSelectorDialog';
import { COLUMN_TYPES } from '../../../data-table/DataTableColumns';
import NgpFormButton from '../../../form/NgpFormButton';
import { filterEntities } from '../../../form/ListSelectorUtils';
import { useArrayLoader } from '../../../data-table/Loaders';
import { SHOULD_DIRTY_TRUE } from '../ServiceTestsFormControlConstants';
import { AGENT_IDS_FIELD_NAME } from '../ServiceTestsConstants';

/**
 * ServiceTestAgentPicker
 *
 * Adapted from AgentSelector for the service test agents.ids nested field structure.
 * Unlike AgentSelector (which hardcodes 'agents' field path), this component reads and
 * writes the provided fieldName directly (e.g. AGENT_IDS_FIELD_NAME).
 *
 * @param {Array} gridColumns - columns for selected table and picker dialog
 * @param {Array} gridPopupColumns - columns for pop up selected table and picker dialog
 * @param {Array} dbSelected - initial selected IDs from DB (used by dialog)
 * @param {string} addButtonText - label for the Add button
 * @param {string} removeButtonText - label for the bulk Remove button
 * @param {string} selectedEmptyGridText - empty state message for selected table
 * @param {boolean} isMultiRemove - whether to show bulk remove checkbox column (default true)
 * @param {Array} selectedAgents - resolved agent objects to display in selected table
 * @param {Function} setTheAgents - parent state setter, called with new IDs array on change
 * @param {Array} sorters - DataTable initial sort config
 * @param {Array} dataArray - available agents shown in picker dialog
 * @param {Array} dataArraySearchFields - fields to use for dialog search
 * @param {Function} setSelectionIsDirty - optional callback, called when selection changes
 */
export function ServiceTestAgentPicker({
    gridColumns,
    gridPopupColumns,
    dbSelected = [],
    addButtonText,
    removeButtonText,
    setSelectionIsDirty,
    isMultiRemove = true,
    selectedEmptyGridText,
    selectedAgents,
    setTheAgents,
    sorters,
    dataArray = null,
    dataArraySearchFields = [],
}) {
    const classes = useSelectorStyles();
    const { setValue, getValues } = useFormContext();
    const selected = useRef(null);
    // update to latest dbSelected
    selected.current = dbSelected;

    const [showDialog, setShowDialog] = useState(false);
    const [theSelectedGridColumns, setTheSelectedGridColumns] = useState(gridColumns);
    const [removeDisabled, setRemoveDisabled] = useState(false);
    const [tableSelection, setTableSelection] = useState(undefined);

    const staticSelectedData = useMemo(() => selectedAgents ?? [], [selectedAgents]);
    const staticDataFilterCallback = useCallback(
        (entities, filters) => filterEntities(entities, filters, dataArraySearchFields),
        [dataArraySearchFields]
    );
    const selectedLoader = useArrayLoader(staticSelectedData, staticDataFilterCallback);

    // removeOne closes over stable RHF refs (getValues/setValue) — safe to compute once
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const deleteColumnDef = useMemo(
        () => [
            {
                type: COLUMN_TYPES.ICON,
                title: '',
                width: 45,
                icons: [
                    {
                        tooltip: t`Delete`,
                        getIcon: () => <DeleteIcon />,
                        clickHandler: removeOne,
                    },
                ],
            },
        ],
        // removeOne is stable after mount; re-creating the column def on every render is unnecessary
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useEffect(() => {
        setTheSelectedGridColumns(
            map([...deleteColumnDef, ...gridColumns], (theColumn) => merge(theColumn, { resizable: false }))
        );
        // Only run once to set up column definitions with delete icon
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTableSelectionChange = (currentTableSelection) => {
        setTableSelection(currentTableSelection);
        setRemoveDisabled(
            !currentTableSelection || (isEmpty(currentTableSelection.selection) && !currentTableSelection.inverted)
        );
    };

    const selectionOptions = isMultiRemove
        ? {
              mode: SELECTIONMODE_MULTI,
              allowSelectAll: true,
              idMapping: '_id',
              onChange: handleTableSelectionChange,
          }
        : { mode: SELECTIONMODE_NONE };

    // Write updated IDs to the form field and notify parent state
    function updateSelection(selectedIds) {
        selected.current = selectedIds;
        setValue(AGENT_IDS_FIELD_NAME, selectedIds, SHOULD_DIRTY_TRUE);
        if (isFunction(setSelectionIsDirty)) {
            setSelectionIsDirty(true);
        }
    }

    // Remove agents matching the current DataTable multi-selection
    function removeSelection() {
        const currentIds = getValues(AGENT_IDS_FIELD_NAME) || [];
        const newIds = currentIds.filter((id) =>
            tableSelection?.inverted ? tableSelection.selection?.includes(id) : !tableSelection.selection?.includes(id)
        );
        if (isFunction(setTheAgents)) setTheAgents(newIds);
        updateSelection(newIds);
    }

    // Remove a single agent by its record object
    function removeOne(record) {
        const currentIds = getValues(AGENT_IDS_FIELD_NAME) || [];
        const newIds = without(currentIds, record._id);
        if (isFunction(setTheAgents)) setTheAgents(newIds);
        updateSelection(newIds);
    }

    // Field registration is owned by UseServiceTestAgentSelector (register({ name: AGENT_IDS_FIELD_NAME }))
    // which stays mounted regardless of mode. A <Controller> wrapper here is harmful because
    // Controller calls unregister() on unmount (shouldUnregister:true), deleting the field
    // from fieldsRef and losing any setValue() changes made by handleModeChange. See NGP-4010.
    return (
        <>
            <div className={classes.selectorContainer}>
                <div className={classes.selectorActions}>
                    <NgpFormButton onClick={() => setShowDialog(true)}>{addButtonText}</NgpFormButton>
                    {isMultiRemove && (
                        <NgpFormButton onClick={removeSelection} disabled={removeDisabled}>
                            {removeButtonText}
                        </NgpFormButton>
                    )}
                </div>
                <DataTable
                    loader={selectedLoader}
                    columns={theSelectedGridColumns}
                    showSelectionColumn={isMultiRemove}
                    disableCSVExport
                    disableColumnRemover
                    sorters={sorters}
                    selectionOptions={selectionOptions}
                    emptyText={selectedEmptyGridText}
                    updateSelection={updateSelection}
                />
            </div>
            <ListSelectorDialog
                gridColumns={gridPopupColumns}
                dbSelected={dbSelected}
                availableSelectAll
                addButtonText={addButtonText}
                showDialog={showDialog}
                setShowDialog={setShowDialog}
                selected={selected.current}
                updateSelection={updateSelection}
                dataArray={dataArray}
                dataArraySearchFields={dataArraySearchFields}
            />
        </>
    );
}

ServiceTestAgentPicker.propTypes = {
    gridColumns: PropTypes.arrayOf(PropTypes.object).isRequired,
    gridPopupColumns: PropTypes.arrayOf(PropTypes.object).isRequired,
    dbSelected: PropTypes.array,
    addButtonText: PropTypes.string.isRequired,
    removeButtonText: PropTypes.string.isRequired,
    setSelectionIsDirty: PropTypes.func,
    isMultiRemove: PropTypes.bool,
    selectedEmptyGridText: PropTypes.string.isRequired,
    selectedAgents: PropTypes.array,
    setTheAgents: PropTypes.func,
    sorters: PropTypes.array,
    dataArray: PropTypes.array,
    dataArraySearchFields: PropTypes.array,
};

ServiceTestAgentPicker.defaultProps = {
    dbSelected: [],
    setSelectionIsDirty: undefined,
    isMultiRemove: true,
    selectedAgents: [],
    setTheAgents: undefined,
    sorters: [],
    dataArray: undefined,
    dataArraySearchFields: [],
};

export default ServiceTestAgentPicker;
